import React, { useEffect, useState } from 'react'
import { Col, OverlayTrigger, Row, Spinner, Tab, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import { Tabs } from '../../../../../components/Tabs/Tabs'
import { ScorecardResult, Scorecard as ScorecardModel } from '../../../../../models/Scorecard'
import pointer from 'jsonpointer'
import { ScorecardBlock } from './ScorecardBlock'
import { FormProvider, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { parseMetadata } from '../utils'
import { ScorecardLabelLevel, sortClientProfiles } from '../../../../../common/clientProfile'
import { User } from '../../../../../models/User'
import { selectClientProfileScorecards, selectClientProfileScorecardsLoading } from '../../../../../store/meetingSummary/clientProfile'

export interface MeetingClientProfileProps {
  contact: User
  scorecardResults: ScorecardResult[]
}

export interface ScorecardResultChangesProps {
  scorecardResult: ScorecardResult
  scorecard: ScorecardModel
  contact: User
  hideClientProfileLink?: boolean
}

export const ScorecardResultChangesForm: React.FC<ScorecardResultChangesProps> = ({ scorecard, scorecardResult, contact, hideClientProfileLink = false }) => {
  const createChangedObject = () => {
    var obj = {}
    Object.entries(scorecardResult.changes).forEach(([key, value]) => {
      if (value['op'] === 'remove') {
        pointer.set(obj, key, null)
      }
      pointer.set(obj, key, value['value'])
    })
    return obj
  }

  const form = useForm({ defaultValues: createChangedObject() })

  const render = (key: string, value: any) => {
    return <ScorecardBlock vkey={key} value={value} scorecard={scorecard} scorecardResult={scorecardResult} />
  }

  const getContactClientProfileLink = () => {
    return `/library/manage-contacts/contacts/${scorecardResult.userId}/tabs/scorecards/${scorecardResult.scorecardId}`
  }

  const shouldRenderBlockLabel = (() => {
    if (!scorecard.metadata) {
      return true
    }
    const metadata = parseMetadata(scorecard.metadata)
    return !(metadata?.labelConfig?.[ScorecardLabelLevel.BLOCK] === 'skip')
  })()

  const showClientProfileLink = contact.roles.some((role) => role.name === 'Client') && !contact.id.startsWith('speaker_') && contact.type !== 'anonymous'

  return (
    <div>
      <Row>
        <Col className='d-flex justify-content-between align-items-center'>
          <div>
            <span className='scorecard-name'>{scorecard.name}</span>
            <OverlayTrigger placement='top' overlay={<BootstrapTooltip className='zocks-text-gray-500'>{scorecard.description}</BootstrapTooltip>}>
              <i className='bi bi-info-square ms-2' />
            </OverlayTrigger>
          </div>
          {!hideClientProfileLink && showClientProfileLink ? (
            <Row className='d-flex flex-column justify-content-end'>
              <Col>
                <Link className='contact-profile-link' to={getContactClientProfileLink()}>
                  Go to contact client profile
                </Link>
              </Col>
            </Row>
          ) : null}
        </Col>
      </Row>
      <Row
        className={classNames('d-flex', {
          'zocks-gap-2.5': shouldRenderBlockLabel,
          'gap-1': !shouldRenderBlockLabel,
        })}
      >
        <FormProvider {...form}>
          {Object.entries(createChangedObject()).map(([k, v]) => {
            return <>{render(k, v)}</>
          })}
        </FormProvider>
      </Row>
    </div>
  )
}

export const Scorecard: React.FC<MeetingClientProfileProps> = ({ contact, scorecardResults }) => {
  const scorecards = useAppSelector(selectClientProfileScorecards)
  const scorecardsLoading = useAppSelector(selectClientProfileScorecardsLoading)
  const [sortedScorecards, setSortedScorecards] = useState<ScorecardModel[]>([])
  const [selectedTab, setSelectedTab] = useState('')

  useEffect(() => {
    if (scorecards) {
      const scorecardIdsWithResult = scorecardResults.map((scorecardResult) => scorecardResult.scorecardId)
      const scorecardsWithResult: ScorecardModel[] = [...scorecards].filter((scorecard) => {
        return scorecardIdsWithResult.includes(scorecard.id)
      })
      const scorecardItems = sortClientProfiles([...scorecardsWithResult])
      setSortedScorecards(scorecardItems)
      if (scorecardItems.length > 0 && !scorecardItems.map((s) => s.id).includes(selectedTab)) {
        changeTab(scorecardItems[0].id)
      }
    }
  }, [scorecards, selectedTab, contact])

  const TabTitle = ({ title }: { title: string }) => <div className='d-flex align-items-center'>{title}</div>

  const changeTab = (key: string | null) => {
    if (key) {
      setSelectedTab(key)
    }
  }

  if (scorecardsLoading) {
    return <Spinner />
  }

  const renderScorecardList = () => {
    if (!scorecards || !scorecardResults) {
      return 'No scorecards available'
    }

    return (
      <div className='d-flex gap-5 scorecards-tab-wrapper'>
        <Tabs onSelect={changeTab} mountOnEnter activeKey={selectedTab} id='scorecards-tabs' className='vertical-tab'>
          {sortedScorecards.map((scorecard) => {
            return (
              <Tab key={scorecard.id} mountOnEnter className='mt-2' eventKey={scorecard.id} title={<TabTitle title={scorecard.name} />}>
                {scorecardResults
                  .filter((scorecardResult) => scorecardResult.scorecardId === scorecard.id)
                  .map((scorecardResult) => {
                    return <ScorecardResultChangesForm scorecard={scorecard} scorecardResult={scorecardResult} contact={contact} key={scorecardResult.id} />
                  })}
              </Tab>
            )
          })}
        </Tabs>
      </div>
    )
  }

  return <>{renderScorecardList()}</>
}
